import {
  DOD_ID_LETTER_DIGIT_PATTERN,
  DOD_ID_VALIDATION_MESSAGE,
  isValidDODID as isValidSharedDODID,
} from '@ths/shared/utils';
import { format } from 'date-fns';
import { FieldValidator } from 'final-form';

import { ClientTeamMemberSelectOption } from 'components/Inputs/FormInputs/ClientTeamMemberSelect';
import { FormValues } from 'pages/GroupRooms';

import { store } from '../store/store';
type ValidatorFunction = (
  value: string | null | object | number | boolean,
  allValues: object | undefined,
) => string | undefined;

export type StringValidatorFunction = (
  value: string | null,
  allValues: object | undefined,
) => string | undefined;

export const required: ValidatorFunction = (
  value: string | null | object | number | boolean,
) => {
  if (typeof value === 'string') {
    return value.trim() ? undefined : 'Required';
  }
  if (typeof value === 'boolean') {
    return undefined;
  }
  if (typeof value === 'number') {
    return !isNaN(value) ? undefined : 'Required';
  }
  return value ? undefined : 'Required';
};

export const enforceLength =
  (length: number): StringValidatorFunction =>
  (value: string | null) =>
    value && value.length !== length
      ? `Must be ${length} characters`
      : undefined;

export const enforceLength4 = enforceLength(4);
export const enforceLength10 = enforceLength(10);

export const validDODID: StringValidatorFunction = (value: string | null) => {
  if (value == null || value === '') {
    return undefined;
  }

  if (!isValidDODID(value)) {
    return `${DOD_ID_VALIDATION_MESSAGE}, or a valid client subgroup`;
  }
};

export const isValidDODID = (dodid?: string | null): boolean => {
  if (!dodid) {
    return false;
  }

  const state = store.getState();
  const clientSubgroups = state.enums.clientSubgroups;
  const validSubgroups = clientSubgroups?.map((subgroup) => subgroup.value);

  if (isValidSharedDODID(dodid, validSubgroups)) {
    return true;
  }

  return false;
};

/**
 * Simple check to determine if input looks like a DoD ID or client subgroup
 * Used for search input validation - less strict than isValidDODID
 */
export const looksLikeDODID = (input?: string | null): boolean => {
  if (!input) return false;
  const trimmed = input.trim();

  // Check if it's numeric (traditional DOD ID)
  if (/^\d+$/.test(trimmed)) {
    return true;
  }

  // Check if it matches the pattern for TXSG format (2 or 3 letters + 4 digits) - case insensitive
  if (DOD_ID_LETTER_DIGIT_PATTERN.test(trimmed)) {
    return true;
  }

  // Check if it's a potential client subgroup (all letters, 3-6 characters) - case insensitive
  if (/^[A-Za-z]{3,6}$/i.test(trimmed)) {
    // Get client subgroups from Redux store if available
    const state = store.getState();
    const clientSubgroups = state.enums.clientSubgroups;
    if (clientSubgroups) {
      const upperTrimmed = trimmed.toUpperCase();
      const validSubgroups = clientSubgroups.map((subgroup) =>
        subgroup.value.toUpperCase(),
      );
      return validSubgroups.includes(upperTrimmed);
    }
    // If store not ready, accept the pattern
    return true;
  }

  return false;
};

// this can be used when we need 2+ validators for an input
export const composeValidators =
  (...validators: Array<StringValidatorFunction>) =>
  (value: string | null, allValues: object | undefined) =>
    validators.reduce(
      (error: string | undefined, validator) =>
        error || validator(value, allValues),
      undefined,
    );

type CheckInOutDates = {
  checkInDate: string;
  checkOutDate: string;
};

export const validateDates = (values: CheckInOutDates) => {
  const errors: Record<string, string> = {};

  if (values.checkInDate && values.checkOutDate) {
    const checkInDate = new Date(values.checkInDate);
    const checkOutDate = new Date(values.checkOutDate);

    if (checkOutDate <= checkInDate) {
      errors.checkOutDate = 'Check-out date must be after check-in date';
    }
  }

  return errors;
};

export const validateRoomQuantities = (
  values: FormValues,
  totalGuests: number,
) => {
  const errors: Record<string, string | Record<string, string>[]> = {};

  // Helper to calculate totals from configurations
  const calculateTotals = (
    configs: Array<{ rooms: string; guests: string }>,
  ) => {
    return configs.reduce(
      (acc, config) => {
        const rooms = Number(config.rooms || 0);
        const guests = Number(config.guests || 0);
        return {
          rooms: acc.rooms + rooms,
          guests: acc.guests + rooms * guests,
        };
      },
      { rooms: 0, guests: 0 },
    );
  };

  // Helper function to validate room configuration
  const validateRoomConfiguration = (
    config: { rooms: string; guests: string },
    roomType: string,
  ): Record<string, string> => {
    const configErrors: Record<string, string> = {};

    if (Number(config.rooms || 0) < 0) {
      configErrors.rooms = 'Cannot have negative rooms';
    }

    const guests = Number(config.guests || 0);
    if ((guests < 1 || guests > 2) && Number(config.rooms || 0) > 0) {
      configErrors.guests = `${roomType} rooms must have 1 or 2 guests`;
    }

    return configErrors;
  };

  // Validate single room configurations
  if (values.single && Array.isArray(values.single)) {
    const singleErrors: Array<Record<string, string>> = [];
    values.single.forEach((config, index) => {
      const configErrors = validateRoomConfiguration(config, 'Single');
      if (Object.keys(configErrors).length > 0) {
        singleErrors[index] = configErrors;
      }
    });
    if (singleErrors.length > 0) errors.single = singleErrors;
  }

  // Validate double room configurations
  if (values.double && Array.isArray(values.double)) {
    const doubleErrors: Array<Record<string, string>> = [];
    values.double.forEach((config, index) => {
      const configErrors = validateRoomConfiguration(config, 'Double');
      if (Object.keys(configErrors).length > 0) {
        doubleErrors[index] = configErrors;
      }
    });
    if (doubleErrors.length > 0) errors.double = doubleErrors;
  }

  // Validate suite room configurations
  if (values.suite && Array.isArray(values.suite)) {
    const suiteErrors: Array<Record<string, string>> = [];
    values.suite.forEach((config, index) => {
      const configErrors = validateRoomConfiguration(config, 'Suite');
      if (Object.keys(configErrors).length > 0) {
        suiteErrors[index] = configErrors;
      }
    });
    if (suiteErrors.length > 0) errors.suite = suiteErrors;
  }

  // Calculate totals
  const singleTotals = calculateTotals(values.single || []);
  const doubleTotals = calculateTotals(values.double || []);
  const suiteTotals = calculateTotals(values.suite || []);

  const totalRooms =
    singleTotals.rooms + doubleTotals.rooms + suiteTotals.rooms;
  const totalConfiguredGuests =
    singleTotals.guests + doubleTotals.guests + suiteTotals.guests;

  // Validate total rooms don't exceed total guests
  if (totalRooms > totalGuests) {
    errors._error = 'Total rooms cannot exceed number of guests';
  }

  // Validate total configured guests don't exceed available guests
  if (totalConfiguredGuests > totalGuests) {
    errors._error = `Total configured guests (${totalConfiguredGuests}) exceeds available guests (${totalGuests})`;
  }

  return errors;
};

// Guest fields are now optional - validation is replaced by a confirmation dialog
// when submitting with an incomplete guest list
export const guestRequired: FieldValidator<
  ClientTeamMemberSelectOption | ''
> = () => {
  return undefined;
};

// Form-level validation for guest quantity (works for both MainHotelSearchForm and HotelSearchBarForm)
export const validateGuestQuantityForm = (values: {
  bookingType?: string;
  guestQuantity?: string;
  roomQuantity?: string;
}) => {
  const errors: Record<string, string> = {};

  // For MainHotelSearchForm: only validate for instant booking
  // For HotelSearchBarForm: always validate (since it doesn't have bookingType)
  const shouldValidate =
    !values.bookingType || values.bookingType === 'instant';

  // First check if guests >= rooms (minimum validation)
  if (
    shouldValidate &&
    values.guestQuantity &&
    values.roomQuantity &&
    Number(values.guestQuantity) < Number(values.roomQuantity)
  ) {
    errors.guestQuantity =
      'Guest quantity must be equal to or greater than room quantity';
    return errors; // Return early to prioritize this error
  }

  // Then ensure guests can be evenly distributed across rooms to prevent API mismatch
  if (
    shouldValidate &&
    values.guestQuantity &&
    values.roomQuantity &&
    Number(values.guestQuantity) % Number(values.roomQuantity) !== 0
  ) {
    errors.guestQuantity =
      'Please adjust guest or room quantity so guests can be evenly distributed across rooms';
  }

  return errors;
};

// Validation for room-based form (works for both group and instant bookings)
export const validateRoomBasedForm = (values: {
  bookingType?: string;
  guestQuantity?: string;
  roomQuantity?: string;
}) => {
  const errors: Record<string, string> = {};
  const minGroupRooms = 9;

  // For group bookings, validate room minimum
  if (values.bookingType === 'group' && values.roomQuantity) {
    const roomCount = Number(values.roomQuantity);
    if (roomCount < minGroupRooms) {
      errors.roomQuantity = `Group trips require a minimum of ${minGroupRooms} rooms`;
    }
  }

  // For instant bookings, validate guest distribution
  if (values.bookingType === 'instant') {
    // First check if guests >= rooms (minimum validation)
    if (
      values.guestQuantity &&
      values.roomQuantity &&
      Number(values.guestQuantity) < Number(values.roomQuantity)
    ) {
      errors.guestQuantity =
        'Guest quantity must be equal to or greater than room quantity';
      return errors; // Return early to prioritize this error
    }

    // Then ensure guests can be evenly distributed across rooms
    if (
      values.guestQuantity &&
      values.roomQuantity &&
      Number(values.guestQuantity) % Number(values.roomQuantity) !== 0
    ) {
      errors.guestQuantity =
        'Please adjust guest or room quantity so guests can be evenly distributed across rooms';
    }
  }

  return errors;
};

// Shared utility functions for hotel search forms
export const getFormattedToday = () => format(new Date(), 'yyyy-MM-dd');

export const getFormattedTomorrow = () =>
  format(new Date(new Date().setDate(new Date().getDate() + 1)), 'yyyy-MM-dd');

export const processInitialDates = (
  checkInDate?: string,
  checkOutDate?: string,
) => {
  const today = getFormattedToday();
  const tomorrow = getFormattedTomorrow();

  const processedCheckInDate =
    checkInDate && checkInDate >= today ? checkInDate : today;

  const processedCheckOutDate =
    checkOutDate && checkOutDate > today ? checkOutDate : tomorrow;

  return {
    checkInDate: processedCheckInDate,
    checkOutDate: processedCheckOutDate,
  };
};

/**
 * Validates email format following RFC 5322 guidelines (simplified)
 * @param email - The email address to validate
 * @returns true if email is valid, false otherwise
 */
export const isValidEmail = (email: string): boolean => {
  // Basic validation checks first
  if (!email || typeof email !== 'string') return false;

  // Don't trim - the email should be valid as-is
  if (email.trim() !== email) return false; // No leading/trailing whitespace
  if (email.length > 254) return false; // RFC 5321 limit
  if (email.startsWith('.') || email.endsWith('.')) return false;
  if (email.includes('..')) return false; // No consecutive dots
  if (email.split('@').length !== 2) return false; // Exactly one @
  if (email.includes(' ')) return false; // No spaces anywhere

  const [localPart, domainPart] = email.split('@');

  // Local part validation
  if (localPart.length === 0 || localPart.length > 64) return false;
  if (localPart.startsWith('.') || localPart.endsWith('.')) return false;

  // Domain part validation
  if (domainPart.length === 0 || domainPart.length > 253) return false;
  if (domainPart.startsWith('.') || domainPart.endsWith('.')) return false;
  if (domainPart.includes('..')) return false;

  // Domain must have at least one dot (TLD requirement)
  if (!domainPart.includes('.')) return false;

  // Check TLD length (should be at least 2 characters)
  const domainParts = domainPart.split('.');
  const tld = domainParts[domainParts.length - 1];
  if (tld.length < 2) return false;

  // More robust regex following RFC 5322 guidelines (simplified)
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

  return emailRegex.test(email);
};

/**
 * Email validation function for use with final-form
 * @param value - The email value to validate
 * @returns Error message if invalid, undefined if valid
 */
export const validateEmail: StringValidatorFunction = (
  value: string | null,
) => {
  if (!value) return undefined; // Allow empty values
  return isValidEmail(value) ? undefined : 'Please enter a valid email address';
};
